import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
@Injectable({
	providedIn: 'root',
})
export class SignalRHubService {
	private connection: signalR.HubConnection | null = null;
	private readonly connectionState$ = new BehaviorSubject<signalR.HubConnectionState>(
		signalR.HubConnectionState.Disconnected
	);
	private readonly pendingListeners = new Map<string, (...args: any[]) => void>();
	private heartbeatHandle: ReturnType<typeof setInterval> | null = null;
	private readonly heartbeatIntervalMs = 45000;
	get state(): signalR.HubConnectionState {
		return this.connection?.state ?? signalR.HubConnectionState.Disconnected;
	}
	get isConnected(): boolean {
		return this.state === signalR.HubConnectionState.Connected;
	}
	initialize(userId: string, clientId: string): void {
		if (this.connection) return;
		const url = new URL(environment.baseUrl);
		const hubIdentifier = url.hostname.split('.')[0].replace('-', '_');
		this.connection = new signalR.HubConnectionBuilder()
			.withUrl(
				`${environment.apiUrl}announcementhub-${hubIdentifier}?userId=${userId}&clientId=${clientId}&hubIdentifier=${hubIdentifier}`
			)
			.configureLogging(signalR.LogLevel.None)
			.withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
			.build();
		this.connection.serverTimeoutInMilliseconds = 60000;
		this.connection.keepAliveIntervalInMilliseconds = 15000;
		this.connection.onreconnected(() => {
			this.connectionState$.next(signalR.HubConnectionState.Connected);
			this.startHeartbeat();
		});
		this.connection.onclose(() => {
			this.connectionState$.next(signalR.HubConnectionState.Disconnected);
			this.stopHeartbeat();
		});
		this.connection
			.start()
			.then(() => {
				this.applyPendingListeners();
				this.connectionState$.next(signalR.HubConnectionState.Connected);
				this.startHeartbeat();
			})
			.catch((err) => {
				console.error('SignalR connection error:', err);
			});
	}

	stop(): void {
		this.stopHeartbeat();
		const connection = this.connection;
		this.connection = null;
		this.connectionState$.next(signalR.HubConnectionState.Disconnected);
		connection?.stop().catch(() => {});
	}

	private startHeartbeat(): void {
		this.stopHeartbeat();
		this.send('ChatPresenceHeartbeat').catch(() => {});
		this.heartbeatHandle = setInterval(() => {
			this.send('ChatPresenceHeartbeat').catch(() => {});
		}, this.heartbeatIntervalMs);
	}

	private stopHeartbeat(): void {
		if (this.heartbeatHandle) {
			clearInterval(this.heartbeatHandle);
			this.heartbeatHandle = null;
		}
	}

	on(eventName: string, callback: (...args: any[]) => void): void {
		this.pendingListeners.set(eventName, callback);
		if (this.connection) {
			this.connection.off(eventName);
			this.connection.on(eventName, callback);
		}
	}

	// Remove a handler registered with on(); call from ngOnDestroy so the closure does not
	// retain the destroyed component.
	off(eventName: string): void {
		this.pendingListeners.delete(eventName);
		this.connection?.off(eventName);
	}

	invoke<T = void>(methodName: string, ...args: any[]): Promise<T> {
		if (!this.isConnected) {
			return Promise.reject(new Error('SignalR not connected'));
		}
		return this.connection!.invoke<T>(methodName, ...args);
	}

	send(methodName: string, ...args: any[]): Promise<void> {
		if (!this.isConnected) {
			return Promise.resolve();
		}
		return this.connection!.send(methodName, ...args);
	}

	whenConnected(): Observable<void> {
		return this.connectionState$.pipe(
			filter((s) => s === signalR.HubConnectionState.Connected),
			map(() => undefined)
		);
	}

	private applyPendingListeners(): void {
		this.pendingListeners.forEach((callback, eventName) => {
			this.connection!.off(eventName);
			this.connection!.on(eventName, callback);
		});
	}
}
